import { useEffect } from 'react';

/**
 * SEOHead - Dynamic SEO meta tags component
 * Updates document head with page-specific SEO information
 */
export const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false,
  structuredData = null
}) => {
  const baseUrl = 'https://www.eventcinema.es';
  const defaultImage = `${baseUrl}/og-image.jpg`;
  
  const fullTitle = title 
    ? `${title} | Event Cinema` 
    : 'Event Cinema - Ópera, Ballet y Conciertos en Directo en tu Cine';
  
  const fullDescription = description || 
    'Disfruta de las mejores producciones de ópera, ballet, conciertos y teatro desde la Royal Opera House, Arena de Verona y más. Entradas con hasta 25% de descuento.';
  
  const fullCanonical = canonicalUrl 
    ? `${baseUrl}${canonicalUrl}` 
    : window.location.href;
  
  const fullOgImage = ogImage || defaultImage;

  useEffect(() => {
    // Update title
    document.title = fullTitle;
    
    // Helper to update or create meta tag
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Primary Meta Tags
    setMeta('description', fullDescription);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', fullDescription, true);
    setMeta('og:url', fullCanonical, true);
    setMeta('og:image', fullOgImage, true);
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', 'Event Cinema', true);
    setMeta('og:locale', 'es_ES', true);
    
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', fullDescription);
    setMeta('twitter:image', fullOgImage);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullCanonical);
    
    // Structured Data
    if (structuredData) {
      // Remove existing structured data for this page
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
    // Cleanup function
    return () => {
      const structuredScript = document.querySelector('script[data-seo-structured]');
      if (structuredScript) {
        structuredScript.remove();
      }
    };
  }, [fullTitle, fullDescription, fullCanonical, fullOgImage, ogType, keywords, noIndex, structuredData]);

  return null; // This component doesn't render anything
};

/**
 * Generate Event structured data for JSON-LD
 */
export const generateEventStructuredData = (event, cinemas = []) => {
  if (!event) return null;
  
  const baseUrl = 'https://www.eventcinema.es';
  
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": event.description?.substring(0, 200) || `${event.title} - Evento cultural en cines`,
    "image": event.poster_url || event.image_url,
    "startDate": event.date,
    "endDate": event.date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": cinemas.length > 0 ? cinemas.map(cinema => ({
      "@type": "Place",
      "name": cinema.cinema_name,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": cinema.city,
        "addressCountry": "ES"
      }
    })) : {
      "@type": "Place",
      "name": "Cines de toda España"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Event Cinema",
      "url": baseUrl
    },
    "performer": event.venue ? {
      "@type": "PerformingGroup",
      "name": event.venue
    } : undefined,
    "offers": {
      "@type": "AggregateOffer",
      "url": `${baseUrl}/evento/${event.id}`,
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "lowPrice": "8",
      "highPrice": "25"
    }
  };
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbStructuredData = (items) => {
  const baseUrl = 'https://www.eventcinema.es';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${baseUrl}${item.url}` : undefined
    }))
  };
};

export default SEOHead;
